import React, { useState, useRef, useMemo } from 'react';
import type { InventoryItem, Order, Recipe, BulkPurchase, OverseasShipment, Supplier } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import AdjustmentsHorizontalIcon from '../components/icons/AdjustmentsHorizontalIcon';
import InventoryItemModal from '../components/inventory/InventoryItemModal';
import AdjustStockModal from '../components/inventory/AdjustStockModal';
import BulkPurchaseModal from '../components/inventory/BulkPurchaseModal';
import InventoryDashboard from '../components/inventory/InventoryDashboard';
import StockTake from '../components/inventory/StockTake';
import DownloadIcon from '../components/icons/DownloadIcon';
import UploadIcon from '../components/icons/UploadIcon';
import MagnifyingGlassIcon from '../components/icons/MagnifyingGlassIcon';
import ShipmentList from '../components/inventory/shipments/ShipmentList';
import CreateShipmentModal from '../components/inventory/shipments/CreateShipmentModal';
import ReceiveShipmentModal from '../components/inventory/shipments/ReceiveShipmentModal';

interface InventoryPageProps {
  inventoryItems: InventoryItem[];
  orders: Order[];
  recipes: Recipe[];
  shipments?: OverseasShipment[];
  suppliers?: Supplier[];
  currentUserId?: number;
  onSave: (item: InventoryItem) => void;
  onDelete: (itemId: number) => void;
  onAdjustStock: (itemId: number, adjustment: number) => void;
  onStockTake: (physicalCounts: { [itemId: number]: number }) => void;
  onBulkAddInventoryItems?: (items: Omit<InventoryItem, 'id' | 'lastUpdated'>[]) => Promise<{ successCount: number, failedItems: string[] }>;
  onSaveShipment?: (shipment: Omit<OverseasShipment, 'id'>) => void;
  onMarkShipmentArrived?: (shipmentId: string) => void;
  onUpdateReceivedQuantities?: (shipmentId: string, items: any[]) => void;
  onCompleteReceiving?: (shipmentId: string, inventoryUpdates: any[]) => void;
  onCancelShipment?: (shipmentId: string) => void;
  onRemoveShipment?: (shipmentId: string) => void;
  onUpdateShipment?: (shipmentId: string, updates: Partial<OverseasShipment>) => void;
  onCreateInventoryItem?: (item: Omit<InventoryItem, 'id' | 'lastUpdated'>) => Promise<InventoryItem>;
  onUpdateInventoryItem?: (itemId: number, updates: Partial<InventoryItem>) => Promise<void>;
  onSaveSupplier?: (supplier: Supplier) => void;
}

const InventoryPage: React.FC<InventoryPageProps> = ({ 
  inventoryItems, 
  orders, 
  recipes, 
  shipments = [],
  suppliers = [],
  currentUserId = 0,
  onSave, 
  onDelete, 
  onAdjustStock, 
  onStockTake, 
  onBulkAddInventoryItems,
  onSaveShipment,
  onMarkShipmentArrived,
  onUpdateReceivedQuantities,
  onCompleteReceiving,
  onCancelShipment,
  onRemoveShipment,
  onUpdateShipment,
  onCreateInventoryItem,
  onUpdateInventoryItem,
  onSaveSupplier,
}) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'list' | 'stock_take' | 'bulk_purchases' | 'overseas_shipments'>('dashboard');
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [isBulkPurchaseModalOpen, setIsBulkPurchaseModalOpen] = useState(false);
  const [isCreateShipmentModalOpen, setIsCreateShipmentModalOpen] = useState(false);
  const [isReceiveShipmentModalOpen, setIsReceiveShipmentModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [selectedBulkPurchase, setSelectedBulkPurchase] = useState<BulkPurchase | null>(null);
  const [selectedShipment, setSelectedShipment] = useState<OverseasShipment | null>(null);
  const [editingShipment, setEditingShipment] = useState<OverseasShipment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Filter states for Item List
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'in_stock' | 'low_stock' | 'out_of_stock'>('all');
  const [filterBulkPurchase, setFilterBulkPurchase] = useState<'all' | 'bulk' | 'regular'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'stock' | 'category' | 'status'>('name');

  const handleOpenItemModal = (item: InventoryItem | null = null) => {
    setSelectedItem(item);
    setIsItemModalOpen(true);
  };
  
  const handleOpenAdjustModal = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsAdjustModalOpen(true);
  };

  const handleOpenBulkPurchaseModal = (item: InventoryItem, bulkPurchase: BulkPurchase | null = null) => {
    // Use a stable reference to prevent unnecessary re-renders
    setSelectedItem({ ...item }); // Create new object reference
    setSelectedBulkPurchase(bulkPurchase ? { ...bulkPurchase } : null); // Create new object reference
    setIsBulkPurchaseModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsItemModalOpen(false);
    setIsAdjustModalOpen(false);
    setIsBulkPurchaseModalOpen(false);
    setSelectedItem(null);
    setSelectedBulkPurchase(null);
  };
  
  const handleSaveItem = (item: InventoryItem) => {
    onSave(item);
    handleCloseModals();
  };

  const handleSaveBulkPurchase = (bulkPurchase: BulkPurchase) => {
    if (!selectedItem) return;
    
    // Check if this is a new bulk purchase (not editing existing)
    const isNewBulkPurchase = !selectedItem.bulkPurchases?.some(bp => bp.id === bulkPurchase.id);
    
    // If it's a new bulk purchase and the item doesn't have bulk purchases yet,
    // reset stock to 0 (nothing allocated yet)
    // If editing existing, preserve current stock
    const newStock = (isNewBulkPurchase && !selectedItem.bulkPurchases?.length) 
      ? 0  // New bulk purchase item - start with 0 allocated stock
      : selectedItem.stock; // Editing - preserve current stock
    
    // Clean the bulkPurchase object to remove undefined values
    const cleanedBulkPurchase: BulkPurchase = {
      id: bulkPurchase.id,
      inventoryItemId: bulkPurchase.inventoryItemId,
      purchaseDate: bulkPurchase.purchaseDate,
      totalQuantity: bulkPurchase.totalQuantity,
      remainingQuantity: bulkPurchase.remainingQuantity,
      weeklyAllocation: bulkPurchase.weeklyAllocation,
    };
    
    // Only add optional fields if they have values
    if (bulkPurchase.bags && bulkPurchase.bags.length > 0) {
      cleanedBulkPurchase.bags = bulkPurchase.bags;
    }
    if (bulkPurchase.notes) {
      cleanedBulkPurchase.notes = bulkPurchase.notes;
    }
    
    const updatedItem: InventoryItem = {
      ...selectedItem,
      isBulkPurchase: true,
      stock: newStock, // Use calculated stock (0 for new bulk purchases)
      bulkPurchases: selectedItem.bulkPurchases 
        ? selectedItem.bulkPurchases.some(bp => bp.id === bulkPurchase.id)
          ? selectedItem.bulkPurchases.map(bp => bp.id === bulkPurchase.id ? cleanedBulkPurchase : bp)
          : [...selectedItem.bulkPurchases, cleanedBulkPurchase]
        : [cleanedBulkPurchase],
    };
    
    // Clean the updated item to remove undefined values
    const cleanedItem: InventoryItem = {
      id: updatedItem.id,
      name: updatedItem.name,
      unit: updatedItem.unit,
      stock: updatedItem.stock,
      lowStockThreshold: updatedItem.lowStockThreshold,
      lastUpdated: updatedItem.lastUpdated,
      isBulkPurchase: true,
      bulkPurchases: updatedItem.bulkPurchases,
    };
    
    // Only add optional fields if they have values
    if (updatedItem.hasExpiryDate) {
      cleanedItem.hasExpiryDate = true;
      if (updatedItem.expiryDate) {
        cleanedItem.expiryDate = updatedItem.expiryDate;
      }
    }
    if (updatedItem.category) {
      cleanedItem.category = updatedItem.category;
    }
    if (updatedItem.supplier) {
      cleanedItem.supplier = updatedItem.supplier;
    }
    if (updatedItem.bulkPurchases) {
      cleanedItem.bulkPurchases = updatedItem.bulkPurchases;
    }
    
    onSave(cleanedItem);
    handleCloseModals();
  };

  const handleDeleteBulkPurchase = (bulkPurchaseId: string) => {
    if (!selectedItem) return;
    
    const updatedItem: InventoryItem = {
      ...selectedItem,
      bulkPurchases: selectedItem.bulkPurchases?.filter(bp => bp.id !== bulkPurchaseId) || [],
      isBulkPurchase: (selectedItem.bulkPurchases?.filter(bp => bp.id !== bulkPurchaseId).length || 0) > 0,
    };
    
    onSave(updatedItem);
  };

  const handleAllocateWeeklyStock = (item: InventoryItem, bulkPurchase: BulkPurchase) => {
    if (bulkPurchase.remainingQuantity < bulkPurchase.weeklyAllocation) {
      alert(`Not enough remaining quantity. Only ${bulkPurchase.remainingQuantity} ${item.unit} remaining.`);
      return;
    }

    let allocationRemaining = bulkPurchase.weeklyAllocation;
    let updatedBags = bulkPurchase.bags ? [...bulkPurchase.bags] : undefined;

    // If using bags, allocate from bags first (FIFO - first in, first out)
    if (updatedBags && updatedBags.length > 0) {
      updatedBags = updatedBags.map(bag => {
        if (allocationRemaining <= 0) return bag;
        
        const allocationFromBag = Math.min(bag.remainingQuantity, allocationRemaining);
        allocationRemaining -= allocationFromBag;
        
        return {
          ...bag,
          remainingQuantity: bag.remainingQuantity - allocationFromBag,
        };
      });
    }

    const updatedBulkPurchase: BulkPurchase = {
      id: bulkPurchase.id,
      inventoryItemId: bulkPurchase.inventoryItemId,
      purchaseDate: bulkPurchase.purchaseDate,
      totalQuantity: bulkPurchase.totalQuantity,
      remainingQuantity: bulkPurchase.remainingQuantity - bulkPurchase.weeklyAllocation,
      weeklyAllocation: bulkPurchase.weeklyAllocation,
    };

    // Only add optional fields if they have values
    if (updatedBags && updatedBags.length > 0) {
      updatedBulkPurchase.bags = updatedBags;
    }
    if (bulkPurchase.notes) {
      updatedBulkPurchase.notes = bulkPurchase.notes;
    }

    const updatedItem: InventoryItem = {
      id: item.id,
      name: item.name,
      unit: item.unit,
      stock: item.stock + bulkPurchase.weeklyAllocation,
      lowStockThreshold: item.lowStockThreshold,
      lastUpdated: Date.now(),
      isBulkPurchase: true,
      bulkPurchases: item.bulkPurchases?.map(bp => bp.id === bulkPurchase.id ? updatedBulkPurchase : bp) || [],
    };

    // Only add optional fields if they have values
    if (item.hasExpiryDate) {
      updatedItem.hasExpiryDate = true;
      if (item.expiryDate) {
        updatedItem.expiryDate = item.expiryDate;
      }
    }
    if (item.category) {
      updatedItem.category = item.category;
    }
    if (item.supplier) {
      updatedItem.supplier = item.supplier;
    }

    onSave(updatedItem);
  };

  const handleDelete = (itemId: number) => {
    if (window.confirm('Are you sure you want to delete this inventory item? This cannot be undone.')) {
      onDelete(itemId);
    }
  };

  const getStatus = (item: InventoryItem) => {
    // For bulk purchase items, check total bulk quantity instead of weekly stock
    let availableQuantity = item.stock;
    if (item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0) {
      // Sum all remaining bulk quantities
      availableQuantity = item.bulkPurchases.reduce((sum, bp) => sum + (bp.remainingQuantity || 0), 0);
    }
    
    if (availableQuantity <= 0) return { text: 'Out of Stock', color: 'bg-red-100 text-red-800', status: 'out_of_stock' as const };
    if (availableQuantity < item.lowStockThreshold) return { text: 'Low Stock', color: 'bg-yellow-100 text-yellow-800', status: 'low_stock' as const };
    return { text: 'In Stock', color: 'bg-green-100 text-green-800', status: 'in_stock' as const };
  };

  // Get unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = new Set<string>();
    inventoryItems.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [inventoryItems]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = inventoryItems.filter(item => {
      // Search filter
      if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Category filter
      if (filterCategory !== 'all' && item.category !== filterCategory) {
        return false;
      }
      
      // Status filter
      if (filterStatus !== 'all') {
        const status = getStatus(item);
        if (status.status !== filterStatus) {
          return false;
        }
      }
      
      // Bulk purchase filter
      if (filterBulkPurchase === 'bulk' && !item.isBulkPurchase) {
        return false;
      }
      if (filterBulkPurchase === 'regular' && item.isBulkPurchase) {
        return false;
      }
      
      return true;
    });

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'stock') {
        let stockA = a.stock;
        let stockB = b.stock;
        if (a.isBulkPurchase && a.bulkPurchases && a.bulkPurchases.length > 0) {
          stockA = a.bulkPurchases.reduce((sum, bp) => sum + (bp.remainingQuantity || 0), 0);
        }
        if (b.isBulkPurchase && b.bulkPurchases && b.bulkPurchases.length > 0) {
          stockB = b.bulkPurchases.reduce((sum, bp) => sum + (bp.remainingQuantity || 0), 0);
        }
        return stockB - stockA; // Descending
      } else if (sortBy === 'category') {
        const catA = a.category || '';
        const catB = b.category || '';
        return catA.localeCompare(catB);
      } else if (sortBy === 'status') {
        const statusA = getStatus(a).status;
        const statusB = getStatus(b).status;
        const statusOrder = { 'out_of_stock': 0, 'low_stock': 1, 'in_stock': 2 };
        return statusOrder[statusA] - statusOrder[statusB];
      }
      return 0;
    });

    return filtered;
  }, [inventoryItems, searchTerm, filterCategory, filterStatus, filterBulkPurchase, sortBy]);

  const handleDownloadTemplate = () => {
    const header = "Name,Stock,Unit,Low Stock Threshold\n";
    const example = "Beef Patties,50,units,20\nTomatoes,10,kg,5\nMilk,9,L,5\n";
    const csvContent = header + example;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "inventory_template.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  };

  const handleDownloadInventory = () => {
    // Create CSV header
    const headers = [
      'Name',
      'Category',
      'Current Stock',
      'Unit',
      'Low Stock Threshold',
      'Status',
      'Bulk Purchase',
      'Total Bulk Quantity',
      'Supplier',
      'Has Expiry Date',
      'Expiry Date',
      'Last Updated'
    ];

    // Convert inventory items to CSV rows
    const rows = inventoryItems.map(item => {
      // Calculate available quantity (for bulk items, use total bulk quantity)
      let availableQuantity = item.stock;
      let totalBulkQuantity = 0;
      if (item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0) {
        totalBulkQuantity = item.bulkPurchases.reduce((sum, bp) => sum + (bp.remainingQuantity || 0), 0);
        availableQuantity = totalBulkQuantity;
      }

      // Determine status
      let status = 'In Stock';
      if (availableQuantity <= 0) {
        status = 'Out of Stock';
      } else if (availableQuantity < item.lowStockThreshold) {
        status = 'Low Stock';
      }

      // Format expiry date
      let expiryDateStr = '';
      if (item.hasExpiryDate) {
        if (item.expiryDate) {
          expiryDateStr = new Date(item.expiryDate).toLocaleDateString();
        } else if (item.expiryDates && item.expiryDates.length > 0) {
          const nearestExpiry = item.expiryDates
            .map(ed => ed.expiryDate)
            .filter(ed => ed > Date.now())
            .sort((a, b) => a - b)[0];
          if (nearestExpiry) {
            expiryDateStr = new Date(nearestExpiry).toLocaleDateString();
          }
        }
      }

      // Escape CSV values (handle commas and quotes)
      const escapeCSV = (value: any): string => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return [
        escapeCSV(item.name),
        escapeCSV(item.category || ''),
        escapeCSV(item.stock.toFixed(2)),
        escapeCSV(item.unit),
        escapeCSV(item.lowStockThreshold),
        escapeCSV(status),
        escapeCSV(item.isBulkPurchase ? 'Yes' : 'No'),
        escapeCSV(item.isBulkPurchase && totalBulkQuantity > 0 ? totalBulkQuantity.toFixed(2) : ''),
        escapeCSV(item.supplier || ''),
        escapeCSV(item.hasExpiryDate ? 'Yes' : 'No'),
        escapeCSV(expiryDateStr),
        escapeCSV(new Date(item.lastUpdated).toLocaleDateString())
      ].join(',');
    });

    // Combine header and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute("href", url);
      link.setAttribute("download", `inventory_export_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onBulkAddInventoryItems) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
        const text = e.target?.result as string;
        if (!text) {
            alert("Error: Could not read the CSV file.");
            return;
        }

        try {
            const rows = text.split(/\r?\n/).filter(row => row.trim() !== '');
            if (rows.length < 2) {
                alert("Error: CSV file is empty or contains only a header. Please add item data below the header row.");
                return;
            }

            const headerRow = rows[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
            const requiredHeaders = ['name', 'stock', 'unit'];
            if (!requiredHeaders.every(h => headerRow.includes(h))) {
                alert(`Invalid CSV format. Header row must contain at least these columns: ${requiredHeaders.join(', ')}`);
                return;
            }
            
            const itemsToAdd: Omit<InventoryItem, 'id' | 'lastUpdated'>[] = [];
            const parsingErrors: string[] = [];

            for (let i = 1; i < rows.length; i++) {
                const values = rows[i].split(',').map(v => v.trim().replace(/"/g, ''));
                try {
                    const nameIndex = headerRow.indexOf('name');
                    const stockIndex = headerRow.indexOf('stock');
                    const unitIndex = headerRow.indexOf('unit');
                    const thresholdIndex = headerRow.indexOf('low stock threshold');

                    if (nameIndex === -1 || stockIndex === -1 || unitIndex === -1) {
                        parsingErrors.push(`Row ${i + 1}: Missing required columns`);
                        continue;
                    }

                    const name = values[nameIndex];
                    const stock = parseFloat(values[stockIndex]);
                    const unit = values[unitIndex];
                    const lowStockThreshold = thresholdIndex !== -1 ? parseFloat(values[thresholdIndex]) : undefined;

                    if (!name || isNaN(stock) || !unit) {
                        parsingErrors.push(`Row ${i + 1}: Invalid data (name: ${name}, stock: ${values[stockIndex]}, unit: ${unit})`);
                        continue;
                    }

                    const validUnits = ['kg', 'g', 'L', 'ml', 'units'];
                    if (!validUnits.includes(unit.toLowerCase())) {
                        parsingErrors.push(`Row ${i + 1}: Invalid unit "${unit}". Must be one of: ${validUnits.join(', ')}`);
                        continue;
                    }

                    itemsToAdd.push({
                        name,
                        stock: Math.max(0, stock),
                        unit: unit.toLowerCase() as 'kg' | 'g' | 'L' | 'ml' | 'units',
                        lowStockThreshold: lowStockThreshold !== undefined ? Math.max(0, lowStockThreshold) : undefined,
                    });
                } catch (error) {
                    parsingErrors.push(`Row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
                }
            }
            
            let feedbackMessages: string[] = [];

            if (itemsToAdd.length > 0) {
              const result = await onBulkAddInventoryItems(itemsToAdd);
              if(result && result.successCount > 0) {
                feedbackMessages.push(`✅ Success: ${result.successCount} items were successfully added.`);
              }
              if(result && result.failedItems && result.failedItems.length > 0) {
                feedbackMessages.push(`⚠️ Failed: Could not add ${result.failedItems.length} items:\n- ${result.failedItems.join('\n- ')}`);
              }
            }

            if (parsingErrors.length > 0) {
              feedbackMessages.push(`❌ Parsing Errors: ${parsingErrors.length} rows had errors:\n- ${parsingErrors.slice(0, 5).join('\n- ')}${parsingErrors.length > 5 ? `\n... and ${parsingErrors.length - 5} more` : ''}`);
            }

            if (feedbackMessages.length > 0) {
              alert(feedbackMessages.join('\n\n'));
            } else {
              alert('No valid items found to add from the CSV file. Please check the file content and format.');
            }

        } catch (error) {
            console.error("Error parsing CSV:", error);
            alert("A critical error occurred while parsing the CSV file. Please check its format.");
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = ''; // Reset file input
            }
        }
    };
    reader.readAsText(file);
  };
  
  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800">Inventory Management</h1>
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleDownloadInventory} 
                className="bg-green-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-300 flex items-center"
                title="Download current inventory as CSV"
              >
                <DownloadIcon className="w-5 h-5 mr-2" /> Export CSV
              </button>
              {onBulkAddInventoryItems && (
                <>
                  <button onClick={handleDownloadTemplate} className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300 flex items-center">
                    <DownloadIcon className="w-5 h-5 mr-2" /> Download Template
                  </button>
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv" className="hidden" />
                  <button onClick={handleUploadClick} className="bg-blue-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-300 flex items-center">
                    <UploadIcon className="w-5 h-5 mr-2" /> Upload CSV
                  </button>
                </>
              )}
              <button
                onClick={() => handleOpenItemModal()}
                className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add New Item
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button onClick={() => setActiveTab('dashboard')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'dashboard' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Dashboard</button>
            <button onClick={() => setActiveTab('list')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'list' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Item List</button>
            <button onClick={() => setActiveTab('bulk_purchases')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'bulk_purchases' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Bulk Purchases</button>
            <button onClick={() => setActiveTab('stock_take')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'stock_take' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Stock Take</button>
            <button onClick={() => setActiveTab('overseas_shipments')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overseas_shipments' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Overseas Shipments</button>
          </nav>
        </div>

        {activeTab === 'dashboard' && <InventoryDashboard inventoryItems={inventoryItems} orders={orders} recipes={recipes} />}

        {activeTab === 'list' && (
          <div className="space-y-4">
            {/* Filter Controls */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Search */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="all">All Status</option>
                    <option value="in_stock">In Stock</option>
                    <option value="low_stock">Low Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                </div>

                {/* Bulk Purchase Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={filterBulkPurchase}
                    onChange={(e) => setFilterBulkPurchase(e.target.value as typeof filterBulkPurchase)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="all">All Types</option>
                    <option value="bulk">Bulk Purchase</option>
                    <option value="regular">Regular</option>
                  </select>
                </div>
              </div>

              {/* Sort and Results Count */}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="name">Name</option>
                    <option value="stock">Stock (High to Low)</option>
                    <option value="category">Category</option>
                    <option value="status">Status</option>
                  </select>
                  {(searchTerm || filterCategory !== 'all' || filterStatus !== 'all' || filterBulkPurchase !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterCategory('all');
                        setFilterStatus('all');
                        setFilterBulkPurchase('all');
                      }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  Showing {filteredItems.length} of {inventoryItems.length} items
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="bg-white shadow rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                      <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry / Bulk</th>
                          <th scope="col" className="relative px-6 py-3">
                              <span className="sr-only">Actions</span>
                          </th>
                      </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {filteredItems.length > 0 ? (
                        filteredItems.map((item) => {
                          const status = getStatus(item);
                          const now = Date.now();
                          let expiryInfo: { text: string; color: string } | null = null;
                          
                          if (item.hasExpiryDate) {
                            if (item.expiryDate) {
                              const daysUntilExpiry = Math.floor((item.expiryDate - now) / (24 * 60 * 60 * 1000));
                              if (daysUntilExpiry >= 0) {
                                expiryInfo = {
                                  text: daysUntilExpiry === 0 ? 'Expires today!' : daysUntilExpiry === 1 ? 'Expires tomorrow' : `${daysUntilExpiry} days`,
                                  color: daysUntilExpiry <= 7 ? 'text-red-600 bg-red-50' : daysUntilExpiry <= 30 ? 'text-yellow-600 bg-yellow-50' : 'text-green-600 bg-green-50',
                                };
                              } else {
                                expiryInfo = { text: 'Expired', color: 'text-red-600 bg-red-100' };
                              }
                            }
                          }
                          
                          const bulkPurchaseInfo = item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0
                            ? item.bulkPurchases.filter(bp => bp.remainingQuantity > 0)
                            : [];
                          
                          return (
                              <tr key={item.id}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                      {item.category && (
                                        <div className="text-xs text-gray-500">{item.category}</div>
                                      )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900 font-semibold">{item.stock.toFixed(2)}</div>
                                      {item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0 && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          📦 {item.bulkPurchases.reduce((sum, bp) => sum + bp.remainingQuantity, 0).toFixed(2)} {item.unit} in bulk storage
                                        </div>
                                      )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{item.unit}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.color}`}>
                                          {status.text}
                                      </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="space-y-1">
                                        {expiryInfo && (
                                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${expiryInfo.color}`}>
                                            {expiryInfo.text}
                                          </span>
                                        )}
                                        {bulkPurchaseInfo.length > 0 && (
                                          <div className="text-xs text-blue-600">
                                            📦 {bulkPurchaseInfo.length} active bulk purchase{bulkPurchaseInfo.length > 1 ? 's' : ''}
                                          </div>
                                        )}
                                      </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      <button onClick={() => handleOpenAdjustModal(item)} className="text-blue-600 hover:text-blue-900 mr-4" title="Adjust Stock">
                                        <AdjustmentsHorizontalIcon className="w-5 h-5" />
                                      </button>
                                      {item.isBulkPurchase && (
                                        <button 
                                          onClick={() => handleOpenBulkPurchaseModal(item)} 
                                          className="text-green-600 hover:text-green-900 mr-4" 
                                          title="Manage Bulk Purchases"
                                        >
                                          <PlusIcon className="w-5 h-5" />
                                        </button>
                                      )}
                                      <button onClick={() => handleOpenItemModal(item)} className="text-pink-600 hover:text-pink-900 mr-4" title="Edit Item">
                                        <EditIcon className="w-5 h-5" />
                                      </button>
                                      <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900" title="Delete Item">
                                        <TrashIcon className="w-5 h-5" />
                                      </button>
                                  </td>
                              </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                            No items found matching your filters. Try adjusting your search criteria.
                          </td>
                        </tr>
                      )}
                  </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'bulk_purchases' && (
          <div className="bg-white shadow rounded-lg overflow-x-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Bulk Purchase Management</h2>
                <p className="text-sm text-gray-500 mt-1">Manage bulk purchases and weekly allocations</p>
              </div>
            </div>
            
            {/* Items with bulk purchases */}
            {inventoryItems.filter(item => item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0).length > 0 && (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purchase Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Quantity</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weekly Allocation</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weeks Left</th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inventoryItems
                    .filter(item => item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0)
                    .flatMap(item => 
                      item.bulkPurchases!.map(bulkPurchase => {
                        const weeksLeft = bulkPurchase.weeklyAllocation > 0 
                          ? Math.floor(bulkPurchase.remainingQuantity / bulkPurchase.weeklyAllocation)
                          : 0;
                        const isLow = weeksLeft < 4;
                        return (
                          <tr key={`${item.id}-${bulkPurchase.id}`} className={isLow ? 'bg-yellow-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.unit}</div>
                            {bulkPurchase.bags && bulkPurchase.bags.length > 0 && (
                              <div className="text-xs text-blue-600 mt-1">
                                📦 {bulkPurchase.bags.length} bag{bulkPurchase.bags.length > 1 ? 's' : ''} × {bulkPurchase.bags[0]?.quantity.toFixed(2)} {item.unit}
                                {bulkPurchase.bags.length > 1 && bulkPurchase.bags.some(b => b.quantity !== bulkPurchase.bags[0].quantity) && (
                                  <span className="text-gray-500 ml-1">(varies)</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {new Date(bulkPurchase.purchaseDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {bulkPurchase.totalQuantity.toFixed(2)} {item.unit}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {bulkPurchase.remainingQuantity.toFixed(2)} {item.unit}
                          </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {bulkPurchase.weeklyAllocation.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`text-sm font-semibold ${isLow ? 'text-red-600' : 'text-gray-700'}`}>
                                ~{weeksLeft} weeks
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleAllocateWeeklyStock(item, bulkPurchase)}
                                  className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 border border-blue-300 rounded hover:bg-blue-50"
                                  title="Allocate this week's stock (moves weekly allocation amount to available stock)"
                                >
                                  Allocate Week
                                </button>
                                <button
                                  onClick={() => handleOpenBulkPurchaseModal(item, bulkPurchase)}
                                  className="text-pink-600 hover:text-pink-900"
                                  title="Edit bulk purchase"
                                >
                                  <EditIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                </tbody>
              </table>
            )}

            {/* Items marked for bulk purchase but without bulk purchase records */}
            {inventoryItems.filter(item => item.isBulkPurchase && (!item.bulkPurchases || item.bulkPurchases.length === 0)).length > 0 && (
              <div className="p-6 border-t border-gray-200">
                <h3 className="text-md font-semibold text-gray-700 mb-4">Items Ready for Bulk Purchase Setup</h3>
                <div className="space-y-3">
                  {inventoryItems
                    .filter(item => item.isBulkPurchase && (!item.bulkPurchases || item.bulkPurchases.length === 0))
                    .map(item => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">Unit: {item.unit} • Current Stock: {item.stock}</div>
                        </div>
                        <button
                          onClick={() => handleOpenBulkPurchaseModal(item)}
                          className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 text-sm font-medium flex items-center gap-2"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add Bulk Purchase
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* No bulk purchase items at all */}
            {inventoryItems.filter(item => item.isBulkPurchase).length === 0 && (
              <div className="p-8 text-center">
                <p className="text-gray-500 mb-4">No items are configured for bulk purchase management.</p>
                <p className="text-sm text-gray-400 mb-4">
                  To get started, edit an inventory item and check "Manage via bulk purchases" in the Additional Options section.
                </p>
                <button
                  onClick={() => {
                    setActiveTab('list');
                    handleOpenItemModal();
                  }}
                  className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 text-sm font-medium"
                >
                  Add New Item
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stock_take' && <StockTake inventoryItems={inventoryItems} onStockTake={onStockTake} />}

        {activeTab === 'overseas_shipments' && (
          <ShipmentList
            shipments={shipments}
            suppliers={suppliers}
            onCreateNew={() => {
              setEditingShipment(null);
              setIsCreateShipmentModalOpen(true);
            }}
            onEdit={(shipment) => {
              setEditingShipment(shipment);
              setIsCreateShipmentModalOpen(true);
            }}
            onView={(shipment) => {
              setSelectedShipment(shipment);
              setIsReceiveShipmentModalOpen(true);
            }}
            onReceive={(shipment) => {
              setSelectedShipment(shipment);
              setIsReceiveShipmentModalOpen(true);
            }}
            onCancel={(shipmentId) => {
              if (onCancelShipment && window.confirm('Are you sure you want to cancel this shipment?')) {
                onCancelShipment(shipmentId);
              }
            }}
            onRemove={onRemoveShipment}
          />
        )}

      </main>
      {isItemModalOpen && (
        <InventoryItemModal 
            item={selectedItem}
            onClose={handleCloseModals}
            onSave={handleSaveItem}
        />
      )}
      {isAdjustModalOpen && selectedItem && (
        <AdjustStockModal
            item={selectedItem}
            onClose={handleCloseModals}
            onAdjust={onAdjustStock}
        />
      )}
      {isBulkPurchaseModalOpen && selectedItem && (
        <BulkPurchaseModal
            item={selectedItem}
            bulkPurchase={selectedBulkPurchase}
            onClose={handleCloseModals}
            onSave={handleSaveBulkPurchase}
            onDelete={handleDeleteBulkPurchase}
        />
      )}
      {isCreateShipmentModalOpen && onSaveShipment && (
        <CreateShipmentModal
          suppliers={suppliers}
          currentUserId={currentUserId}
          shipment={editingShipment || undefined}
          onClose={() => {
            setIsCreateShipmentModalOpen(false);
            setEditingShipment(null);
          }}
          onSave={(shipment) => {
            if (onSaveShipment) {
              onSaveShipment(shipment);
            }
            setIsCreateShipmentModalOpen(false);
            setEditingShipment(null);
          }}
          onUpdate={onUpdateShipment || undefined}
          onSaveSupplier={onSaveSupplier}
        />
      )}
      {isReceiveShipmentModalOpen && selectedShipment && onMarkShipmentArrived && onUpdateReceivedQuantities && onCompleteReceiving && onCreateInventoryItem && onUpdateInventoryItem && (
        <ReceiveShipmentModal
          shipment={selectedShipment}
          inventoryItems={inventoryItems}
          suppliers={suppliers}
          currentUserId={currentUserId}
          onClose={() => {
            setIsReceiveShipmentModalOpen(false);
            setSelectedShipment(null);
          }}
          onMarkArrived={onMarkShipmentArrived}
          onUpdateReceivedQuantities={onUpdateReceivedQuantities}
          onCompleteReceiving={onCompleteReceiving}
          onCreateInventoryItem={onCreateInventoryItem}
          onUpdateInventoryItem={onUpdateInventoryItem}
        />
      )}
    </div>
  );
};

export default InventoryPage;